import { h } from 'hyperapp';
import {
    DESKTOP_BREAKPOINT,
    TABLET_BREAKPOINT,
    LARGE_DESKTOP_BREAKPOINT,
} from '../common/constants';

const LARGE_DESKTOP_QUERY = `(min-width: ${LARGE_DESKTOP_BREAKPOINT}px)`;
const DESKTOP_QUERY = `(min-width: ${DESKTOP_BREAKPOINT + 1}px)`;
const TABLET_QUERY = `(min-width: ${TABLET_BREAKPOINT}px)`;

const largeDesktopMediaQueryObject = window.matchMedia(LARGE_DESKTOP_QUERY);
const desktopMediaQueryObject = window.matchMedia(DESKTOP_QUERY);
const tabletMediaQueryObject = window.matchMedia(TABLET_QUERY);

export const NOT_DESKTOP_RESOLUTION = 'non-desktop';
export const MOBILE_RESOLUTION = 'mobile';
export const TABLET_RESOLUTION = 'tablet';
export const DESKTOP_RESOLUTION = 'desktop';
export const LARGE_DESKTOP_RESOLUTION = 'large-desktop';

const getResolution = () => {
    if (window.innerWidth > DESKTOP_BREAKPOINT) {
        return DESKTOP_RESOLUTION;
    }
    if (window.innerWidth >= TABLET_BREAKPOINT) {
        return TABLET_RESOLUTION;
    }
    return MOBILE_RESOLUTION;
};

let currentResolution =
    window.innerWidth > DESKTOP_BREAKPOINT ? DESKTOP_RESOLUTION : NOT_DESKTOP_RESOLUTION;

let largeDesktopListeners = [];
let nonDesktopListeners = [];
let desktopListeners = [];
let tabletListeners = [];
let mobileListeners = [];

const listen = mediaQuery => {
    if (
        mediaQuery.matches &&
        getResolution() === DESKTOP_RESOLUTION &&
        mediaQuery.media === DESKTOP_QUERY
    ) {
        currentResolution = DESKTOP_RESOLUTION;
        desktopListeners.forEach(listener => listener());
        return;
    }
    if (getResolution() === TABLET_RESOLUTION) {
        currentResolution = NOT_DESKTOP_RESOLUTION;
        [...new Set([...nonDesktopListeners, ...tabletListeners])].forEach(listener => listener());
        return;
    }
    if (
        !mediaQuery.matches &&
        getResolution() === MOBILE_RESOLUTION &&
        mediaQuery.media === TABLET_QUERY
    ) {
        currentResolution = NOT_DESKTOP_RESOLUTION;
        [...new Set([...nonDesktopListeners, ...mobileListeners])].forEach(listener => listener());
    }
};

const largeDesktopListen = () => {
    largeDesktopListeners.forEach(listener => listener());
};

largeDesktopMediaQueryObject.addListener(largeDesktopListen);
desktopMediaQueryObject.addListener(listen);
tabletMediaQueryObject.addListener(listen);

export const firstResizeListen = () => {
    largeDesktopListen(largeDesktopMediaQueryObject);
    listen(desktopMediaQueryObject);
    listen(tabletMediaQueryObject);
};

const addListener = resolution => listener => {
    if (resolution === LARGE_DESKTOP_RESOLUTION) {
        largeDesktopListeners.push(listener);
    } else if (resolution === DESKTOP_RESOLUTION) {
        desktopListeners.push(listener);
    } else if (resolution !== LARGE_DESKTOP_RESOLUTION) {
        nonDesktopListeners.push(listener);
        if (getResolution() === TABLET_RESOLUTION) {
            tabletListeners.push(listener);
        } else if (getResolution() === MOBILE_RESOLUTION) {
            mobileListeners.push(listener);
        }
    }

    return {
        unsubscribe() {
            if (resolution === LARGE_DESKTOP_BREAKPOINT) {
                largeDesktopListeners = largeDesktopListeners.filter(l => l !== listener);
            } else if (resolution === DESKTOP_RESOLUTION) {
                desktopListeners = desktopListeners.filter(l => l !== listener);
            } else if (resolution !== LARGE_DESKTOP_RESOLUTION) {
                nonDesktopListeners = nonDesktopListeners.filter(l => l !== listener);
                if (getResolution() === TABLET_RESOLUTION) {
                    tabletListeners = tabletListeners.filter(l => l !== listener);
                } else if (getResolution() === MOBILE_RESOLUTION) {
                    mobileListeners = mobileListeners.filter(l => l !== listener);
                }
            }
        },
    };
};

export const getCurrentResolution = () => currentResolution;

export const onSwitchToLargeDesktop = addListener(LARGE_DESKTOP_RESOLUTION);
export const onSwitchFromDesktop = addListener(NOT_DESKTOP_RESOLUTION);
export const onSwitchToDesktop = addListener(DESKTOP_RESOLUTION);
export const onSwitchTablet = addListener(NOT_DESKTOP_RESOLUTION);

export const resolutionState = {
    mobileObserver: {
        resolution: NOT_DESKTOP_RESOLUTION,
    },
};

export const resolutionActions = {
    mobileObserver: {
        setResolution: value => state => ({ ...state, resolution: value }),
    },
};

export const Desktop = ({ render }) => (state, actions) => {
    function listener(mq) {
        if (mq.matches) {
            actions.mobileObserver.setResolution(DESKTOP_RESOLUTION);
        } else {
            actions.mobileObserver.setResolution(NOT_DESKTOP_RESOLUTION);
        }
    }

    return (
        <div
            oncreate={() => {
                desktopMediaQueryObject.addListener(listener);
                listener(desktopMediaQueryObject);
            }}
            ondestroy={() => {
                desktopMediaQueryObject.removeListener(listener);
            }}
        >
            {render(state.mobileObserver.resolution === DESKTOP_RESOLUTION)}
        </div>
    );
};
